import React from 'react'
import { Box, Link as MuiLink, TextBody, TextHeading } from '@ally/metronome-ui'
import styled from 'styled-components'
import { useAccountTypes } from '../providers'
import { useHostServices } from '../HostServices'

const StyledList = styled.ul`
  list-style-type: none;
`

const StyledMuiLink = styled(MuiLink)`
  font-size: 30px;
`

export const HAS_VISITED_DASHBOARD_ERROR_KEY = 'HAS_VISITED_DASHBOARD_ERROR'

const DashboardError: React.FC = () => {
  const {
    isBankUser,
    isCCOnlyUser,
    isCCUser,
    isCCFullOutage,
    isInvestOnlyUser,
    isInvestOutage,
    isInvestUser,
    isMortgageUser,
    isDebtPlanUser,
  } = useAccountTypes()

  const { browserHistory } = useHostServices()

  // state is populated from DebtPlan.tsx in the browserHistory.push
  const debtPlanOnlyUser = !!(browserHistory.location.state as {
    debtPlanOnlyUser: boolean
  })?.debtPlanOnlyUser

  sessionStorage.setItem(HAS_VISITED_DASHBOARD_ERROR_KEY, '1')

  function calculateBankPlanText(): string {
    if (debtPlanOnlyUser) {
      return 'Plans:'
    }

    if (isBankUser && isDebtPlanUser) {
      return 'Bank Accounts and Plans:'
    }

    return 'Bank Accounts:'
  }

  return (
    <Box textAlign="center">
      {isInvestOutage && isInvestOnlyUser ? (
        <>
          <TextHeading tag="h1" size="md">
            We can’t show these accounts right now.
          </TextHeading>
          <TextHeading tag="h2" size="md" weight="normal">
            We’re working to have things back up as soon as possible. Try again
            later.
          </TextHeading>
        </>
      ) : (
        <>
          <TextHeading tag="h1" size="md">
            We’re not able to show your accounts right now.
          </TextHeading>
          {isCCFullOutage && isCCOnlyUser ? (
            <TextHeading tag="h2" size="md" weight="normal">
              Try again later or call us at{' '}
              <StyledMuiLink
                href="tel:18883662559"
                aria-label="1, 8 8 8, 3 6 6, 2 5 5 9"
                text="1-888-366-2559"
                variant="link"
              />{' '}
              for help. We’re here 24/7.
            </TextHeading>
          ) : (
            <TextHeading tag="h2" size="md" weight="normal">
              Try again later, or call us for help.
            </TextHeading>
          )}
        </>
      )}

      <TextBody tag="p" size="md">
        <StyledList>
          {isInvestUser && (
            <li>
              <strong>Investment Accounts:</strong>{' '}
              <MuiLink
                href="tel:18558802559"
                aria-label="1, 8 5 5, 8 8 0, 2 5 5 9"
                text="1-855-880-2559"
                variant="link"
              />{' '}
              <TextBody tag="span" size="sm">
                We’re here 24/7.
              </TextBody>
            </li>
          )}
          {(isBankUser || debtPlanOnlyUser) && (
            <li>
              <strong>{calculateBankPlanText()}</strong>{' '}
              <MuiLink
                href="tel:18772472559"
                aria-label="1, 8 7 7, 2 4 7, 2 5 5 9"
                text="1-877-247-2559"
                variant="link"
              />{' '}
              <TextBody tag="span" size="sm">
                We’re here 24/7.
              </TextBody>
            </li>
          )}
          {isCCUser && !isCCOnlyUser && (
            <li>
              <strong>Credit Card:</strong>{' '}
              <MuiLink
                href="tel:18883662559"
                aria-label="1, 8 8 8, 3 6 6, 2 5 5 9"
                text="1-888-366-2559"
                variant="link"
              />{' '}
              <TextBody tag="span" size="sm">
                We’re here 24/7.
              </TextBody>
            </li>
          )}
          {isMortgageUser && (
            <li>
              <strong>Home Loans:</strong>{' '}
              <MuiLink
                href="tel:18552562559"
                aria-label="1, 8 5 5, 2 5 6, 2 5 5 9"
                text="1-855-256-2559"
                variant="link"
              />{' '}
              <TextBody tag="span" size="sm">
                We’re here Mon - Fri, 8:30 am - 8 pm and Sat, 8:30 am - 1 pm ET.
              </TextBody>
            </li>
          )}
        </StyledList>
      </TextBody>
    </Box>
  )
}

export default DashboardError
